import React from 'react';
import { Card } from '@/components/ui/card';
import { formatPrice } from '@/config/pricing';
import type { WalletTransaction } from '@/types';

// Simple relative time formatter (replaces date-fns)
const formatRelativeTime = (date: Date): string => {
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) return 'just now';
  if (diffInSeconds < 3600) {
    const mins = Math.floor(diffInSeconds / 60);
    return `${mins} ${mins === 1 ? 'minute' : 'minutes'} ago`;
  }
  if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600);
    return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
  }
  if (diffInSeconds < 604800) {
    const days = Math.floor(diffInSeconds / 86400);
    return `${days} ${days === 1 ? 'day' : 'days'} ago`;
  }
  if (diffInSeconds < 2592000) {
    const weeks = Math.floor(diffInSeconds / 604800);
    return `${weeks} ${weeks === 1 ? 'week' : 'weeks'} ago`;
  }
  if (diffInSeconds < 31536000) {
    const months = Math.floor(diffInSeconds / 2592000);
    return `${months} ${months === 1 ? 'month' : 'months'} ago`;
  }
  const years = Math.floor(diffInSeconds / 31536000);
  return `${years} ${years === 1 ? 'year' : 'years'} ago`;
};

interface WalletTransactionHistoryProps {
  transactions: WalletTransaction[];
  isLoading?: boolean;
}

export const WalletTransactionHistory: React.FC<WalletTransactionHistoryProps> = ({
  transactions,
  isLoading = false,
}) => {
  if (isLoading) {
    return (
      <Card className="p-4">
        <h3 className="text-base font-semibold text-slate-900 mb-3">Transaction History</h3>
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="animate-pulse flex items-center gap-3 p-2">
              <div className="flex-1 space-y-1">
                <div className="h-3 bg-slate-200 rounded w-1/3" />
                <div className="h-2 bg-slate-200 rounded w-1/4" />
              </div>
              <div className="h-4 bg-slate-200 rounded w-16" />
            </div>
          ))}
        </div>
      </Card>
    );
  }

  if (transactions.length === 0) {
    return (
      <Card className="p-4">
        <h3 className="text-base font-semibold text-slate-900 mb-3">Transaction History</h3>
        <div className="text-center py-8">
          <p className="text-sm text-slate-600">No transactions yet</p>
          <p className="text-xs text-slate-500 mt-1">
            Add funds to your wallet to get started
          </p>
        </div>
      </Card>
    );
  }

  const getTypeLabel = (type: WalletTransaction['type'], paymentMethod?: string) => {
    switch (type) {
      case 'topup':
        return 'Added funds';
      case 'deduction':
        return 'Wallet deduction';
      case 'bonus':
        return 'Bonus';
      case 'refund':
        return 'Refund';
      case 'payment':
        return paymentMethod === 'one-time-payment' ? 'One-time payment' : 'Payment';
    }
  };

  return (
    <Card className="p-4">
      <h3 className="text-base font-semibold text-slate-900 mb-3">Transaction History</h3>

      <div className="space-y-0">
        {transactions.map((transaction) => (
          <div
            key={transaction.id}
            className="flex items-center gap-3 py-2.5 border-b border-slate-100 last:border-0"
          >
            {/* Details */}
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-slate-900 truncate">
                {transaction.description}
              </p>
              <div className="flex items-center gap-2 mt-0.5">
                <p className="text-xs text-slate-500">
                  {formatRelativeTime(new Date(transaction.createdAt))}
                </p>
                <span className="text-xs text-slate-400">•</span>
                <p className="text-xs text-slate-500">
                  {getTypeLabel(transaction.type, transaction.metadata?.paymentMethod)}
                </p>
              </div>
            </div>

            {/* Amount */}
            <div className="flex-shrink-0 text-right">
              <p className={`text-sm font-semibold ${
                transaction.amount >= 0 ? 'text-green-600' : 'text-red-600'
              }`}>
                {transaction.amount >= 0 ? '+' : ''}
                {formatPrice(Math.abs(transaction.amount))}
              </p>
              <p className="text-xs text-slate-500">
                Balance: {transaction.balanceAfter !== null && transaction.balanceAfter !== undefined ? formatPrice(transaction.balanceAfter) : '-'}
              </p>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
};
